import React, { useCallback, useEffect } from 'react';

import ReferralContent from './ReferralContent';
import Loading from '@/components/elements/Loading';
import NotFound from '@/components/pages/NotFound';
import useSafeParams from '@/hooks/useSafeParams';
import { useReferralDashboardContext } from '@/modules/ce/components/referral/ReferralDashboard';
import { ReferralRoutes } from '@/routes/routes';
import { useGetCeReferralQuery } from '@/types/gqlTypes';
import { generateSafePath } from '@/utils/pathEncoding';

/**
 * Thin wrapper around ReferralContent for Referrals rendered in the Referral context
 * (what we previously called "floating referrals").
 */
const ReferralPage: React.FC = () => {
  const { referralId } = useSafeParams() as { referralId: string };

  const { overrideBreadcrumbTitles } = useReferralDashboardContext();

  const {
    data: { ceReferral: referral } = {},
    loading,
    error,
  } = useGetCeReferralQuery({
    variables: {
      id: referralId,
    },
  });

  useEffect(() => {
    if (!referral) return;
    overrideBreadcrumbTitles({
      [ReferralRoutes.REFERRAL]: `${referral.targetProjectName} - ${referral.clientName}`,
    });
  }, [referral, overrideBreadcrumbTitles]);

  const overrideStepTitle = useCallback(
    (name: string | undefined) => {
      if (name === undefined) return;
      overrideBreadcrumbTitles({ [ReferralRoutes.REFERRAL_STEP]: name });
    },
    [overrideBreadcrumbTitles]
  );

  if (loading) return <Loading />;
  if (error) throw error;
  if (!referral) return <NotFound />;

  return (
    <ReferralContent
      referral={referral}
      referralPath={generateSafePath(ReferralRoutes.REFERRAL, {
        referralId: referral.id,
      })}
      generateReferralStepPath={(stepId) =>
        generateSafePath(ReferralRoutes.REFERRAL_STEP, {
          referralId: referral.id,
          stepId,
        })
      }
      overrideStepTitle={overrideStepTitle}
      linkToProject
    />
  );
};

export {
  type ReferralContext,
  useReferralContext,
} from './referralOutletContext';
export default ReferralPage;
